import { getUnprocessableErrors } from '@10xlabs/utils'
import { ApolloError } from '@apollo/client'
import { FieldArray, Formik, useFormikContext } from 'formik'
import { isEmpty } from 'lodash'
import { ReactElement } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink } from 'react-router-dom'

import { Button } from 'components/v2/Button'
import { Column } from 'components/v2/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { Input } from 'components/Input'
import { Row } from 'components/v2/Row'
import { IBusinessClient, INewBusinessSiteValues } from 'features/BusinessSites/types'
import { BusinessClientAutocomplete } from 'features/BusinessClients/components/BusinessClientAutocomplete'
import { ContactsFormCard } from 'features/BusinessSites/components/ContactsFormCard'
import { IBusinessSiteContact } from 'features/BusinessSites/types'
import { GTBusinessClientAutocomplete } from 'gql/types.generated'
import { schema } from './schema'
import { mapContactsToForm } from 'features/BusinessSites/utils'
import { AddCircleIcon } from 'components/Icons'
import IconButton from 'components/v2/IconButton'
import KreditsCategoryForm from 'features/Kredits/components/KreditsCategoryForm'
import styled from 'styled-components'
import { DEFAULT_KREDITS_SETTINGS_VALUES } from 'features/BusinessClients/utils'
import { CityAutocomplete } from 'features/Cities/components/CityAutocomplete'

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const messages = defineMessages({
  namePlaceholder: {
    id: 'businessSites.form.name.placeholder',
    defaultMessage: 'Enter the site name'
  },
  clientPlaceholder: {
    id: 'businessSites.form.client.placeholder',
    defaultMessage: 'Select a business client'
  }
})

interface IGeneralFormCardProps {
  isEdit?: boolean
  isBusinessClientDisabled?: boolean
}

const GeneralFormCard = ({ isEdit, isBusinessClientDisabled }: IGeneralFormCardProps): ReactElement => {
  const intl = useIntl()
  const { values, handleChange, handleBlur, setFieldValue, touched, errors } =
    useFormikContext<INewBusinessSiteValues>()

  return (
    <FormLayout.Card small>
      <Heading.H4>
        <FormattedMessage id="businessSites.form.section.general.title" defaultMessage="General information" />
      </Heading.H4>
      <Row>
        <Column width="50%">
          <BusinessClientAutocomplete
            id="client"
            value={values.client.name}
            onChange={(businessClient: GTBusinessClientAutocomplete): void => {
              setFieldValue('client', businessClient)
            }}
            placeholder={intl.formatMessage(messages.clientPlaceholder)}
            label={<FormattedMessage id="businessSites.form.client.label" defaultMessage="Business client" />}
            touched={touched.client?.id}
            errors={errors.client?.id}
            disabled={isEdit || isBusinessClientDisabled}
          />
        </Column>
        <Column width="50%">
          <Input
            id="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={intl.formatMessage(messages.namePlaceholder)}
            label={<FormattedMessage id="businessSites.form.name.label" defaultMessage="Name" />}
            touched={touched.name}
            errors={errors.name}
          />
        </Column>
      </Row>
      <Row>
        <Column width="50%">
          <CityAutocomplete
            id="city"
            value={values.city.name}
            onChange={(city): void => {
              setFieldValue('city', city)
            }}
            onBlur={handleBlur}
            placeholder={intl.formatMessage(messages.clientPlaceholder)}
            label={<FormattedMessage id="businessSites.form.city.label" defaultMessage="City" />}
          />
        </Column>
      </Row>
    </FormLayout.Card>
  )
}

interface INewBusinessSiteProps {
  businessClient?: IBusinessClient
  businessSite?: INewBusinessSiteValues
  onSubmit: (values: INewBusinessSiteValues) => void
  loading?: boolean
  error?: ApolloError
  isEdit?: boolean
}

export const NewBusinessSite = ({
  businessClient,
  businessSite,
  onSubmit,
  loading,
  error,
  isEdit
}: INewBusinessSiteProps): ReactElement => {
  const intl = useIntl()

  const initialValues: INewBusinessSiteValues = {
    kreditsSettings: businessSite?.kreditsSettings ?? DEFAULT_KREDITS_SETTINGS_VALUES,
    name: businessSite?.name ?? '',
    client: businessClient ?? {
      id: '',
      name: ''
    },
    city: {
      id: businessSite?.city?.id ?? '',
      name: businessSite?.city?.name ?? '',
      stateCode: businessSite?.city?.stateCode ?? ''
    },
    contacts: (businessSite?.contacts as IBusinessSiteContact[])
      ? mapContactsToForm(businessSite?.contacts as IBusinessSiteContact[])
      : [
          {
            name: '',
            phone: '',
            secondaryPhone: '',
            email: '',
            secondaryEmail: ''
          }
        ]
  }

  const canAddKreditsCategory = true //TODO: ADD PERMISSIONS https://kolors.atlassian.net/browse/NA-14421
  return (
    <Formik initialValues={initialValues} validationSchema={schema(intl, initialValues)} onSubmit={onSubmit}>
      {({ handleSubmit, dirty }): ReactElement => {
        return (
          <FormLayout.Content as="form" onSubmit={handleSubmit}>
            <Heading.H3>
              {isEdit ? (
                <FormattedMessage id="businessSites.edit.title" defaultMessage="Edit business site" />
              ) : (
                <FormattedMessage id="businessSites.new.title" defaultMessage="Add business site" />
              )}
            </Heading.H3>
            <GeneralFormCard isEdit={isEdit} isBusinessClientDisabled={!isEmpty(businessClient)} />
            <ContactsFormCard />
            <FieldArray
              name="kreditsSettings.categories"
              render={(arrayHelpers): ReactElement => {
                return (
                  <FormLayout.Card>
                    <FormLayout.CardHeader>
                      <Heading.H4>
                        <FormattedMessage
                          id="businessClient.form.kreditSettings"
                          defaultMessage="Client Kredit Settings <i>Optional</i>"
                          values={{
                            i: (chunks) => <i>{chunks}</i>
                          }}
                        />
                      </Heading.H4>
                      <Actions>
                        <IconButton
                          icon={<AddCircleIcon />}
                          onClick={(): void => {
                            arrayHelpers.push(DEFAULT_KREDITS_SETTINGS_VALUES.categories[0])
                          }}
                          disabled={!canAddKreditsCategory}
                        />
                      </Actions>
                    </FormLayout.CardHeader>

                    <KreditsCategoryForm fieldName={`kreditsSettings.categories`} />
                  </FormLayout.Card>
                )
              }}
            />

            {error && (
              <FormLayout.Actions>
                <ErrorMessage errors={getUnprocessableErrors(error)}>
                  <FormattedMessage
                    id="businessSites.generalForm.error"
                    defaultMessage="There was an error while creating your site"
                  />
                </ErrorMessage>
              </FormLayout.Actions>
            )}
            <FormLayout.Actions>
              <Button as={NavLink} kind="cancel" to="/business-sites" disabled={loading}>
                <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
              </Button>
              <Button type="submit" kind="primary" disabled={!dirty} loading={loading}>
                <FormattedMessage id="dictionary.save" defaultMessage="Save" />
              </Button>
            </FormLayout.Actions>
          </FormLayout.Content>
        )
      }}
    </Formik>
  )
}
